import { Observable } from "rxjs";
import { DateTime } from 'luxon'

import { SearchQuery, DataStoreOptions, DataSynchronisationStatus, DataSynchronizerStats, SearchResponse } from "@skinsoft/types";
import { AbstractSyncDownHandler } from "../abstract";
import { DataStore } from "@skinsoft/data-store";
import { OfflineDataFetcher } from "@skinsoft/offline";
import { cleanAssociatedMedias } from "@skinsoft/utils";

/**
 * @type T Type of entity fetched from API
 * @type R Type of results from response of synch down request
 * @type U type of entity to save defaults to R
 */
export class EntitySyncDownHandler<T extends { id: string }, R extends { id: string }, U = R> extends AbstractSyncDownHandler<R, SearchResponse<R>, U> {
    protected dataStore: DataStore<T, R>
    protected offlineDataFetcher: OfflineDataFetcher<T, R>
    protected cacheKey: string

    /**
     * 
     * @param dataStore 
     * @param offlineDataFetcher 
     * @param cacheKey 
     */
    constructor(typesForSynchDown: string[], dataStore: DataStore<T, R>, offlineDataFetcher: OfflineDataFetcher<T, R>, cacheKey: string, searchPageSize?: number) {
        super(typesForSynchDown, searchPageSize)
        this.dataStore = dataStore
        this.offlineDataFetcher = offlineDataFetcher
        this.cacheKey = cacheKey
    }

    get mainCacheKey(): string {
        return this.cacheKey
    }

    async clearCacheDown(): Promise<void> {
        await this.offlineDataFetcher.clear()
    }

    async beforeSynchDown(): Promise<void> {
        this.executionStartTime = DateTime.now()
        this.shouldAbortSync = false
        this.currentNbSychronized = 0
        this.syncing = true
        this.saveStats(DataSynchronisationStatus.SUCCESS) 
    }

    sendSynchroDownRequest(searchQuery: SearchQuery, options: DataStoreOptions): Observable<SearchResponse<R>> {
        return this.dataStore.paginatedSearch(searchQuery, options)
    }

    getTotalNbToSync(response: SearchResponse<R>): number {
        return response.totalSize
    }

    getNbResultsInResponse(response: SearchResponse<R>): number {
        return response.results.length
    }

    unpackSynchDownResponse(response: SearchResponse<R>): R[] {
        return response.results
    }
    
    formatDataForCaching(rawData: R[]): U[] {
        return rawData.map(entity => {
            // @ts-ignore
            if(entity.associatedMedias) {
                // @ts-ignore
                entity.associatedMedias = cleanAssociatedMedias(entity.associatedMedias)
            }

            return entity
        }) as unknown as U[]
    }

    async saveSynchDownDataInCache(data: U[]): Promise<void> {
        await this.offlineDataFetcher.append(data as unknown as R[])
    }

    saveStats(status: DataSynchronisationStatus): void {
        this.offlineDataFetcher.saveStats({
            currentNbSychronized: this.currentNbSychronized,
            lastSynchroDate: DateTime.now().toMillis(),
            lastSynchroDuration: this.executionStartTime ? this.executionStartTime.diffNow().toMillis() : 0,
            lastSynchroStatus: status,
            maxNbToSynchronize: this.maxNbToSynchronize,
        })
    }

    abortSynchronisation(): void {
        this.shouldAbortSync = true
    }

    isSyncing(): boolean {
        return this.syncing
    }

    getLastSyncStats(): DataSynchronizerStats | null {
        return this.offlineDataFetcher.getLastSynchroStats()
    }
}